import React from "react";
import classNames from "classnames";

export type OnKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
) => void;

type TextInputPropsBase = {
    id?: string;
    disabled?: boolean;
    onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onChange: (value: string) => void;
    onKeyDown?: OnKeyDown;
    placeholder?: string;
    size?: "xsmall" | "small" | "medium" | "large" | "xlarge";
    value?: string | number;
    className?: string;
    tabIndex?: number;
    label?: React.ReactNode;
    description?: string;
    error?: string;
    icon?: string;
    fullWidth?: boolean;
    type?: string;
    autoFocus?: boolean;
    min?: number;
    max?: number;
    readOnly?: boolean;
    hideText?: boolean;
    disableAutoComplete?: boolean;
    dark?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "onChange" | "value">;

type TextInputPropsMultiline = TextInputPropsBase & {
    multiline: number;
    textRef?: React.LegacyRef<HTMLTextAreaElement>;
};

type TextInputPropsSingleLine = TextInputPropsBase & {
    multiline?: undefined;
    textRef?: React.LegacyRef<HTMLInputElement>;
};

type TextInputProps = TextInputPropsMultiline | TextInputPropsSingleLine;

export default function TextInput({
    id,
    disabled = false,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    placeholder,
    value,
    size = "medium",
    className,
    multiline,
    tabIndex,
    label,
    description,
    textRef,
    error,
    icon,
    fullWidth = false,
    type = "text",
    min,
    max,
    autoFocus = false,
    readOnly = false,
    hideText = false,
    disableAutoComplete = false,
    dark = false
}: TextInputProps) {
    // Handle value conversion and null/undefined cases
    const stringValue = React.useMemo(() => {
        if (value === undefined || value === null) return "";
        return String(value);
    }, [value]);

    // Handle change events
    const handleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChange(event.target.value);
        },
        [onChange]
    );

    if (multiline) {
        return (
            <div className={classNames("flex flex-col gap-1", { "w-full": fullWidth })}>
                <div className="textInput flex flex-col gap-2 border-gray-400">
                    {label && (
                        <div
                            className={classNames(
                                "inputLabel text-sm font-medium",
                                dark ? "text-gray-200" : "text-gray-700"
                            )}
                        >
                            {label}
                        </div>
                    )}
                    {description && <div className="text-sm text-secondary">{description}</div>}
                    <textarea
                        id={id}
                        ref={textRef as React.LegacyRef<HTMLTextAreaElement>}
                        className={classNames(
                            "input2",
                            dark && "dark",
                            error && "border-pink-600",
                            size,
                            className
                        )}
                        disabled={disabled}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        onChange={handleChange}
                        placeholder={placeholder}
                        value={stringValue}
                        tabIndex={tabIndex}
                        style={{
                            height: 18 + 20 * multiline,
                            ...(hideText
                                ? ({ WebkitTextSecurity: "disc" } as React.CSSProperties)
                                : {})
                        }}
                        autoFocus={autoFocus}
                        readOnly={readOnly}
                        autoComplete={disableAutoComplete ? "off" : undefined}
                        data-1p-ignore={disableAutoComplete ? "true" : undefined}
                        data-lpignore={disableAutoComplete ? "true" : undefined}
                    />
                </div>
                {error && <div className="pl-1 text-xs text-pink-600">{error}</div>}
            </div>
        );
    }

    return (
        <div className={classNames("flex flex-col gap-1", { "w-full": fullWidth })}>
            <div className="flex flex-col gap-2">
                {label && (
                    <div
                        className={classNames(
                            "text-sm font-medium",
                            dark ? "text-gray-200" : "text-gray-700"
                        )}
                    >
                        {label}
                    </div>
                )}
                {description && <div className="text-xs text-secondary">{description}</div>}
                {icon ? (
                    <div className="inputContainer">
                        <i className={classNames(icon, dark ? "text-gray-400" : "")}></i>
                        <input
                            id={id}
                            ref={textRef as React.LegacyRef<HTMLInputElement>}
                            className={classNames(
                                "input2",
                                dark ? "dark" : "",
                                size,
                                className,
                                error && "border-pink-600"
                            )}
                            disabled={disabled}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            onKeyDown={onKeyDown}
                            onChange={handleChange}
                            placeholder={placeholder}
                            type={type}
                            value={stringValue}
                            tabIndex={tabIndex}
                            style={{
                                ...(hideText
                                    ? ({ WebkitTextSecurity: "disc" } as React.CSSProperties)
                                    : {})
                            }}
                            min={min}
                            max={max}
                            readOnly={readOnly}
                            autoFocus={autoFocus}
                            autoComplete={disableAutoComplete ? "off" : undefined}
                            data-1p-ignore={disableAutoComplete ? "true" : undefined}
                            data-lpignore={disableAutoComplete ? "true" : undefined}
                        />
                    </div>
                ) : (
                    <input
                        id={id}
                        ref={textRef as React.LegacyRef<HTMLInputElement>}
                        className={classNames(
                            "input2",
                            dark ? "dark" : "",
                            size,
                            className,
                            error && "border-pink-600"
                        )}
                        disabled={disabled}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        onChange={handleChange}
                        placeholder={placeholder}
                        type={type}
                        value={stringValue}
                        tabIndex={tabIndex}
                        style={{
                            ...(hideText
                                ? ({ WebkitTextSecurity: "disc" } as React.CSSProperties)
                                : {})
                        }}
                        min={min}
                        max={max}
                        readOnly={readOnly}
                        autoFocus={autoFocus}
                        autoComplete={disableAutoComplete ? "off" : undefined}
                        data-1p-ignore={disableAutoComplete ? "true" : undefined}
                        data-lpignore={disableAutoComplete ? "true" : undefined}
                    />
                )}
            </div>
            {error && <div className="pl-1 text-xs text-pink-600">{error}</div>}
        </div>
    );
}
